import { create } from 'zustand';
import type { Me, Org } from '@/lib/orgs-api';
import { VERNE_ORG_ID } from '@/lib/orgs-api';

// Persist verne admins' selected org across reloads so swapping tabs or
// reloading doesn't bounce them back to org 1.
const SELECTED_ORG_KEY = 'verne.selectedOrgId';

function loadSelectedOrgId(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SELECTED_ORG_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// Write the selected org to localStorage *without* updating React state.
// Used by the org switcher right before a full page reload — touching the
// store would trigger consumers (e.g. RobotsGate) to re-run their effects
// against the new orgId on the still-mounted page and race the navigation.
export function persistSelectedOrgId(id: number | null): void {
  saveSelectedOrgId(id);
}

function saveSelectedOrgId(id: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id == null) window.localStorage.removeItem(SELECTED_ORG_KEY);
    else window.localStorage.setItem(SELECTED_ORG_KEY, String(id));
  } catch {
    /* quota or disabled storage — non-fatal */
  }
}

interface OrgStore {
  me: Me | null;
  orgs: Org[];
  // verne admins can override the default org_id from /me. null = use /me's org_id.
  selectedOrgId: number | null;

  setMe: (me: Me | null) => void;
  setOrgs: (orgs: Org[]) => void;
  setSelectedOrgId: (id: number | null) => void;
}

export const useOrgStore = create<OrgStore>((set) => ({
  me: null,
  orgs: [],
  selectedOrgId: loadSelectedOrgId(),

  setMe: (me) => set({ me }),
  setOrgs: (orgs) => set({ orgs }),
  setSelectedOrgId: (selectedOrgId) => {
    saveSelectedOrgId(selectedOrgId);
    set({ selectedOrgId });
  },
}));

export function getEffectiveOrgId(): number | null {
  const { me, selectedOrgId } = useOrgStore.getState();
  return selectedOrgId ?? me?.org_id ?? null;
}

export function useEffectiveOrgId(): number | null {
  return useOrgStore((s) => s.selectedOrgId ?? s.me?.org_id ?? null);
}

export function useIsVerne(): boolean {
  return useOrgStore((s) => s.me?.org_id === VERNE_ORG_ID);
}

export function useEffectiveOrgName(): string | null {
  return useOrgStore((s) => {
    const effective = s.selectedOrgId ?? s.me?.org_id ?? null;
    if (effective == null) return null;
    return s.orgs.find((o) => o.id === effective)?.name ?? null;
  });
}
